import React, { useEffect, useRef, useState } from "react";
import { Flex, Typography, theme } from "antd";

const { Text } = Typography;

type LoadingProps = {
  fullPage?: boolean;
  tip?: string;
};

// ── Auto tips ─────────────────────────────────────────────────────────────────

const AUTO_TIPS = [
  "Cargando información...",
  "Verificando datos...",
  "Preparando vista...",
  "Casi listo...",
];

// ── Ledger SVG ────────────────────────────────────────────────────────────────

const LedgerWave: React.FC<{ color: string; trackColor: string }> = ({
  color,
  trackColor,
}) => (
  <svg width="52" height="44" viewBox="0 0 52 44" fill="none">
    {[0, 10, 20, 30].map((y, i) => (
      <React.Fragment key={y}>
        {/* Ghost track */}
        <rect x={0} y={y} width={52} height={6} rx={3} fill={trackColor} />
        {/* Animated fill */}
        <rect x={0} y={y} width={52} height={6} rx={3} fill={color}>
          <animate
            attributeName="width"
            values="0;52;0"
            dur="2s"
            begin={`${i * 0.25}s`}
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
          />
        </rect>
      </React.Fragment>
    ))}
    {/* Total underline */}
    <rect x={0} y={40} width={52} height={4} rx={2} fill={color} opacity={0.25}>
      <animate
        attributeName="opacity"
        values="0.15;0.5;0.15"
        dur="2s"
        repeatCount="indefinite"
      />
    </rect>
  </svg>
);

// ── Progress & tip ────────────────────────────────────────────────────────────

const ProgressInfo: React.FC<{ tip: string }> = ({ tip }) => {
  const [progress, setProgress] = useState(0);
  const [autoTipIdx, setAutoTipIdx] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        const next = Math.min(prev + Math.random() * 6 + 2, 95);
        setAutoTipIdx(Math.min(Math.floor(next / 25), AUTO_TIPS.length - 1));
        return next;
      });
    }, 350);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return (
    <Flex vertical align="center" gap={4}>
      <Text style={{ fontSize: 13, fontWeight: 600 }}>
        {tip || AUTO_TIPS[autoTipIdx]}
      </Text>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {Math.round(progress)}%
      </Text>
    </Flex>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const Loading: React.FC<LoadingProps> = ({ fullPage = true, tip = "" }) => {
  const { token } = theme.useToken();

  useEffect(() => {
    if (!fullPage) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [fullPage]);

  const card = (
    <Flex
      vertical
      align="center"
      gap={20}
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 20,
        padding: "36px 48px",
        boxShadow: token.boxShadowSecondary,
        minWidth: 200,
      }}
    >
      <LedgerWave
        color={token.colorPrimary}
        trackColor={token.colorBorderSecondary}
      />
      <ProgressInfo tip={tip} />
    </Flex>
  );

  if (fullPage) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: `${token.colorBgLayout}cc`,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        }}
      >
        {card}
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      justify="center"
      style={{ width: "100%", minHeight: 300 }}
    >
      {card}
    </Flex>
  );
};

export default Loading;
